"use client";

import { useEffect, useRef, type FC } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { flyToWithCardRoom, HIGHLIGHT_ZOOM, LOCATION_ZOOM, type Coordinates } from "../../lib/map-options";
import type { PinnedPractitioner } from "./practitioner-markers";

type MapCameraProps = {
  pinned: PinnedPractitioner[];
  center: Coordinates | null;
  selected: PinnedPractitioner | null;
  locationZoom?: number;
  /** Screen-px kept clear for the selected pin's card; negative for the mobile bottom carousel */
  cardClearanceY?: number;
};

export const MapCamera: FC<MapCameraProps> = ({
  pinned,
  center,
  selected,
  locationZoom = LOCATION_ZOOM,
  cardClearanceY,
}) => {
  const map = useMap();
  const hasFitBoundsRef = useRef(false);

  /* Refs so a breakpoint flip doesn't re-run the camera effects and discard the user's pan */
  const locationZoomRef = useRef(locationZoom);
  const cardClearanceYRef = useRef(cardClearanceY);
  useEffect(() => {
    locationZoomRef.current = locationZoom;
    cardClearanceYRef.current = cardClearanceY;
  }, [locationZoom, cardClearanceY]);

  useEffect(() => {
    if (!map || hasFitBoundsRef.current || center || pinned.length === 0) return;

    hasFitBoundsRef.current = true;
    const bounds = new google.maps.LatLngBounds();
    pinned.forEach(({ coordinates }) => bounds.extend(coordinates));
    map.fitBounds(bounds, 48);
  }, [map, center, pinned]);

  useEffect(() => {
    if (!map || !center) return;

    hasFitBoundsRef.current = true;
    map.panTo(center);
    map.setZoom(locationZoomRef.current);
  }, [map, center]);

  useEffect(() => {
    if (!map || !selected) return;

    return flyToWithCardRoom(map, selected.coordinates, HIGHLIGHT_ZOOM, cardClearanceYRef.current);
  }, [map, selected]);

  return null;
};
